import React, { useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import { useAnalytics } from '../../queries/stats';
import { Card, CardHead, PageHeader, RangeSelect, Toolbar } from '../../components';
import { P } from '../../theme/tokens';
import DailyBars from './charts/DailyBars';
import WeeklyBars from './charts/WeeklyBars';
import { byCaller, kpiTiles, RANGES, rangeParams, toPercents } from './data';
import { downloadCsv } from './exportCsv';
import {
  BarRow,
  BarRows,
  Footer,
  Four,
  Funnel,
  FunnelRow,
  KpiCard,
  KpiGrid,
  MiniLabel,
  Note,
  OutlineButton,
  Stack,
  Two
} from './styled';

const CALLER_COLORS = [P.navy, P.info, P.success, P.accent, P.purple, P.cyan];

function WhoCalled({ rows }) {
  const data = toPercents(byCaller(rows));
  if (!data.length) return <Note>No caller data yet.</Note>;
  return (
    <BarRows>
      {data.map((r, i) => (
        <BarRow key={r.label} $color={CALLER_COLORS[i % CALLER_COLORS.length]}>
          <div className="top">
            <span>{r.label}</span>
            <b>{r.pct}%</b>
          </div>
          <div className="track">
            <div className="fill" style={{ width: `${r.pct}%` }} />
          </div>
        </BarRow>
      ))}
    </BarRows>
  );
}

function LeasingFunnel({ stages }) {
  const max = Math.max(1, ...stages.map((s) => s.value));
  if (!stages.some((s) => s.value > 0)) return <Note>No leasing activity in this range.</Note>;
  return (
    <Funnel>
      {stages.map((s) => (
        <FunnelRow key={s.key} $pct={Math.max(14, (s.value / max) * 100)}>
          <span className="bar">{s.value}</span>
          <span className="label">{s.label}</span>
        </FunnelRow>
      ))}
    </Funnel>
  );
}

function MiniCard({ title, values, unit, color }) {
  return (
    <Card>
      <CardHead>
        <h2 style={{ fontSize: 14 }}>{title}</h2>
      </CardHead>
      <WeeklyBars values={values} unit={unit} color={color} />
      <MiniLabel>
        {values.join(', ')}
        {unit ? ` ${unit}` : ''} by week
      </MiniLabel>
    </Card>
  );
}

export default function Reports() {
  const [rangeKey, setRangeKey] = useState('30d');
  const range = RANGES.find((r) => r.key === rangeKey);
  const params = useMemo(() => rangeParams(range.days), [range.days]);
  const { data, isPending, isError } = useAnalytics(params);

  const kpis = kpiTiles(data?.kpis);
  const series = data?.series || [];
  const weekly = data?.weekly || {};

  const minis = [
    { key: 'tours', title: 'Tour requests', values: weekly.tours, color: P.info },
    { key: 'emergencies', title: 'Emergencies', values: weekly.emergencies, color: P.danger },
    weekly.accept
      ? { key: 'accept', title: 'Median time to accept', values: weekly.accept, unit: 'min', color: P.warning }
      : null,
    { key: 'workOrders', title: 'Work orders', values: weekly.workOrders, color: P.navy }
  ].filter((m) => m && Array.isArray(m.values));

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Calls, leasing and operations across your portfolio" />

      <Toolbar>
        <RangeSelect>
          <FontAwesomeIcon icon={faCalendarDays} />
          <select
            aria-label="Date range"
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value)}
          >
            {RANGES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
        </RangeSelect>
      </Toolbar>

      {isError && (
        <Alert type="error" showIcon message="Couldn't load analytics." style={{ marginBottom: 16 }} />
      )}

      <Spin spinning={isPending}>
        <Stack aria-busy={isPending}>
          <KpiGrid>
            {kpis.map((k) => (
              <KpiCard key={k.label}>
                <div className="eyebrow">{k.label}</div>
                <div className="num">{k.value}</div>
                {k.sub && <div className="sub">{k.sub}</div>}
              </KpiCard>
            ))}
          </KpiGrid>

          <Card>
            <CardHead>
              <h2>Calls per day</h2>
              <span>{range.label.toLowerCase()}</span>
            </CardHead>
            {series.length ? <DailyBars series={series} /> : <Note>No calls in this range.</Note>}
            <Footer>
              <OutlineButton type="button" disabled={isPending || !series.length} onClick={() => downloadCsv(series)}>
                <FontAwesomeIcon icon={faFileArrowDown} style={{ fontSize: 11 }} />
                Export for owner reporting
              </OutlineButton>
            </Footer>
          </Card>

          <Two>
            <Card>
              <CardHead>
                <h2>Who called</h2>
                <span>by caller type</span>
              </CardHead>
              <WhoCalled rows={data?.byCallerRole || []} />
            </Card>
            <Card>
              <CardHead>
                <h2>Leasing funnel</h2>
                <span>this range</span>
              </CardHead>
              <LeasingFunnel stages={data?.funnel || []} />
            </Card>
          </Two>

          {minis.length > 0 && (
            <Four>
              {minis.map((m) => (
                <MiniCard key={m.key} title={m.title} values={m.values} unit={m.unit} color={m.color} />
              ))}
            </Four>
          )}

          <Note>
            Measured from Call Logs, bookings and work orders. Office time handled is an estimate from
            total talk time.
          </Note>
        </Stack>
      </Spin>
    </div>
  );
}
